import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'
import type { ReactNode } from 'react'

interface Props {
  open: boolean
  title: string
  /** A string is wrapped for you; pass a node when the detail needs structure. */
  body: ReactNode
  confirmLabel: string
  /** True while the action is in flight: both buttons lock and Escape is ignored. */
  busy: boolean
  /** Colours the confirm button red. For things that cannot be undone. */
  destructive?: boolean
  onCancel: () => void
  onConfirm: () => void
}

/**
 * The one confirmation dialog.
 *
 * Every irreversible or spending action in the app routes through here, so the
 * wording, the button order and the busy behaviour cannot drift between screens
 * -- the account actions and the queue actions were written weeks apart and
 * would otherwise each have their own idea of what "are you sure" looks like.
 *
 * Cancel is on the left and unemphasised; the action itself carries the label
 * saying what it does ("Delete", "Power On") rather than a bare "OK", so the
 * button is readable without the title.
 */
export default function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  busy,
  destructive = false,
  onCancel,
  onConfirm,
}: Props) {
  return (
    <Dialog
      open={open}
      // Not dismissible mid-flight: closing the dialog would leave the action
      // running with nothing on screen accounting for it.
      onClose={busy ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {typeof body === 'string' ? <DialogContentText>{body}</DialogContentText> : body}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={destructive ? 'error' : 'primary'}
          onClick={onConfirm}
          disabled={busy}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
